import { useCompoundInterestsSimulatorForm } from "@/hooks/simulators/useCompoundInterestsSimulatorForm";
import { getSimulation } from "@/hooks/wealthSimulator/useWealthSimulatorCalculation";
import { getFormattedValue } from "@/utils/simulators";
import { useEffect, useRef } from "react";
import { FormattedMessage } from "react-intl";
import { styled } from "stitches.config";
import * as $segment from "@/utils/segment";
import { SimulatorActionBlock } from "../simulators/SimulatorActionBlock";
import { SimulatorShareResult } from "../simulators/SimulatorShareResult";
import Typography from "../sushi/Typography";
import { CompoundInterestsForm } from "./CompoundInterestsForm";
import { CompoundInterestsGraph, CompoundInterestsGraphData } from "./CompoundInterestsGraph";
import { CompoundInterestsResultValues } from "./CompoundInterestsResultValues";

const SIMULATOR_NAME = 'compound_interests';

export const CompoundInterestsSimulatorSection = () => {
    const {
      form,
      isInvalid,
      compoundPeriodsPerYear,
      initialCapital,
      interestRate,
      monthlySavings,
      numbersOfYears } = useCompoundInterestsSimulatorForm();

    useEffect(() => {
        $segment.event('simulator_started', { simulator: SIMULATOR_NAME });
    }, []);

    const resultsTrackedRef = useRef(false);
    useEffect(() => {
        if (!isInvalid && !resultsTrackedRef.current) {
            resultsTrackedRef.current = true;
            $segment.event('simulator_results_shown', { simulator: SIMULATOR_NAME });
        }
    }, [isInvalid]);

    const { addedValue: interests, netValue } = getSimulation(initialCapital, numbersOfYears, monthlySavings * 12, interestRate * 100, 0, 0, compoundPeriodsPerYear);

    const totalPayments = initialCapital + monthlySavings * 12 * numbersOfYears;

    const graphData = [...new Array(Math.floor(numbersOfYears))].map((_, index): CompoundInterestsGraphData => {
        const year = index + 1;
        const { addedValue: yearInterests, netValue: yearNetValue } = getSimulation(initialCapital, year, monthlySavings * 12, interestRate * 100, 0, 0, compoundPeriodsPerYear);
        const payments = initialCapital + monthlySavings * 12 * year;

        return {
            year,
            interests: yearInterests,
            payments,
            netValue: yearNetValue,
        }
    });



    return (
      <Container>
        <FormContainer>
          <CompoundInterestsForm register={form.register} />
          <SimulatorActionBlock utmCampaign="simulators_tools_compoundinterests" />
        </FormContainer>
        <GraphContainer>
          <CompoundInterestsResultValues endCapital={netValue} interests={interests} payments={totalPayments} isInvalid={isInvalid} />
          <CompoundInterestsGraph nbYears={numbersOfYears} graphData={graphData} displayPlaceholder={isInvalid} />
          <InfosContainer>
            <Typography color="secondary">
              <FormattedMessage
                id="compound_interests_simulator.infos"
                values={{
                // @ts-expect-error
                monthlySavings: () => <Typography as="span" color="primary">{getFormattedValue(monthlySavings, isInvalid)}</Typography>,
                // @ts-expect-error
                yearsNbr: () => <Typography as="span" color="primary">{numbersOfYears}</Typography>,
                // @ts-expect-error
                interestRate: () => (
                  <Typography as="span" color="primary">
                    {parseFloat((interestRate * 100).toFixed(2))}%
                  </Typography>
                ),
                // @ts-expect-error
                endCapital: () => <Typography as="span" color="primary">{getFormattedValue(netValue)}</Typography>,
                  // @ts-expect-error
                initialCapital: () => <Typography as="span" color="primary">{getFormattedValue(initialCapital)}</Typography>,
              }}
        />
            </Typography>
            <SimulatorShareResult />
          </InfosContainer>
        </GraphContainer>
      </Container>
    )
}

const Container = styled('section', {
    backgroundColor: 'black',
    border: '1px solid $borderOpacity',
    borderRadius: '$4',
    width: '100%',
    maxWidth: '1032px',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    '@sm': {
      gridTemplateColumns: '1fr',
    }
  });

  const FormContainer = styled('div', {
    borderRight: '1px solid $borderSecondary',
  });

  const GraphContainer = styled('div', {
    display: 'flex',
    flexDirection: 'column',
    gap: '$10',
    padding: '$10 0',
  });


  const InfosContainer = styled('div', {
    display: 'flex',
    flexDirection: 'column',
    gap: '$6',
    padding: '0 $10',
  })

